import React, { useEffect, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import { useDispatch, useSelector } from 'react-redux';
import {
  createNotes,
  defiTransactionsSelector,
  getDefiTransaction,
  updateNotes,
} from '../../../store/defi/transactionsSlice';
import { getDateTimeFromTimestamp } from '../../../utilities/datetime';
import { getNumbersWithDecimalPlaces } from '../../../utilities/money';
import AccountSvg from '../../../assets/accounts';
import debounce from 'lodash.debounce';

const CustomDataTableCell = ({ children, ...props }) => {
  return (
    <DataTableCell title={`${children}`} {...props}>
      <p>{children ? getDateTimeFromTimestamp(Number(children) * 1000) : ''}</p>
    </DataTableCell>
  );
};

const CustomDataTableNotes = ({ children, ...props }) => {
  const [editedValue, setEditedValue] = useState(children ? children.note : '');
  const dispatch = useDispatch();

  const addUpdateNotesHandler = useCallback(
    debounce((values) => dispatch(values.id ? updateNotes(values) : createNotes(values)), 2000),
    []
  );

  const onCellEditData = (item, data) => {
    setEditedValue(item);
    if (!item) return;
    const values = {
      note: {
        note: item,
        entity_id: data.item._id,
        type: 'transaction',
      },
      id: children ? children.id : null,
    };
    addUpdateNotesHandler(values);
  };

  return (
    <DataTableCell
      title={`${children}`}
      {...props}
      className={`${!children && !editedValue && 'bg-yellow'}`}
    >
      <input
        type="text"
        value={editedValue}
        onChange={(e) => {
          onCellEditData(e.target.value, props);
        }}
        className={`editable-cell-input ${!children && !editedValue && 'bg-yellow'}`}
        // onBlur={(e) => onCellEditData(e.target.value, props)}
      />
    </DataTableCell>
  );
};
CustomDataTableNotes.displayName = DataTableCell.displayName;

// Custom data table amount cell
const CustomDataTableAmountCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={`${children}`} {...props}>
      {item.amount && (
        <span className="slds-m-right_xx-small">
          <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        </span>
      )}

      {item.amount && <>{`$${getNumbersWithDecimalPlaces(item.amount, 4)}`}</>}
    </DataTableCell>
  );
};
CustomDataTableAmountCell.displayName = DataTableCell.displayName;

// Custom data table gas cell
const CustomDataTableGasCell = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      {item.eth_gas_fee && item.usd_gas_fee && (
        <span className="slds-m-right_xx-small">
          <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        </span>
      )}

      {item.eth_gas_fee && item.usd_gas_fee && (
        <>{`${getNumbersWithDecimalPlaces(item.eth_gas_fee, 4)} ETH ($${getNumbersWithDecimalPlaces(
          item.usd_gas_fee,
          4
        )})`}</>
      )}
    </DataTableCell>
  );
};
CustomDataTableGasCell.displayName = DataTableCell.displayName;

const CustomDataTableCellOne = ({ children, ...props }) => {
  return (
    <DataTableCell title={`${children}`} {...props}>
      {children && (
        <span className="slds-m-right_xx-small">
          <img src="/assets/icons/menus/transactions.svg" alt=""></img>
        </span>
      )}

      {children}
    </DataTableCell>
  );
};
CustomDataTableCellOne.displayName = DataTableCell.displayName;

CustomDataTableCell.displayName = DataTableCell.displayName;

const DataTableAccountColumn = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      <Link to={`/defi/${item.account}`}>
        <span className="slds-m-right_xx-small">
          <AccountSvg color={item.account_color || '#CCCCCC'} />
        </span>
        {item.account_name ? item.account_name : children}
      </Link>
    </DataTableCell>
  );
};

DataTableAccountColumn.displayName = DataTableCell.displayName;

const DataTableTypeColumn = ({ children, ...props }) => {
  const { item } = props;
  let type = children || item?.tx?.name;
  type = type ? type[0].toUpperCase() + type.slice(1) : '';

  return (
    <DataTableCell title={type} {...props}>
      {type}
    </DataTableCell>
  );
};

DataTableTypeColumn.displayName = DataTableCell.displayName;

const CustomDataTableChain = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      <div className="slds-m-right_xx-small" style={{ display: 'flex', gap: '5px' }}>
        {item?.chain_logo && <img src={item.chain_logo} alt="" height="20" width="20"></img>}
        {item.chain_name}
      </div>
    </DataTableCell>
  );
};
CustomDataTableChain.displayName = DataTableCell.displayName;

const TransactionHistory = ({ account }) => {
  const dispatch = useDispatch();
  const { transactions, loading, pagination } = useSelector(defiTransactionsSelector);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortColumnDirection, setSortColumnDirection] = useState({
    opportunityName: 'asc',
  });

  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [paginated, setPaginated] = useState(0);

  useEffect(() => {
    dispatch(getDefiTransaction({ account: account }));
  }, [account, dispatch]);

  useEffect(() => {
    if (transactions.length > 0) setItems(transactions);
  }, [transactions]);

  const handleSort = (sortColumn, ...rest) => {
    const sortProperty = sortColumn.property;

    const { sortDirection } = sortColumn;

    setSortColumn(sortProperty);
    setSortColumnDirection({
      [sortProperty]: sortDirection,
    });

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };

  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      setIsPageLoading(true);
      setTimeout(() => {
        if (pagination?.pages <= 1 || paginated >= pagination?.pages) {
          setHasMore(false);
          setIsPageLoading(false);
          return;
        }

        if (pagination.currentPage >= pagination.pages) {
          setHasMore(false);
          setIsPageLoading(false);
          return;
        }
        let url = new URLSearchParams(pagination.nextPageUrl);
        setPaginated(paginated + 1);
        dispatch(
          getDefiTransaction({ account, limit: url.get('limit'), offset: url.get('offset') })
        );
        setIsPageLoading(false);
      }, 1000);
    }
  };

  return (
    <div style={{ display: 'flex', gap: '1rem' }}>
      <div style={{ marginTop: '10px' }}>
        <DataTable
          assistiveText={{
            actionsHeader: 'actions',
            columnSort: 'sort this column',
            columnSortedAscending: 'asc',
            columnSortedDescending: 'desc',
            selectAllRows: 'Select all rows',
            selectRow: 'Select this row',
          }}
          fixedHeader
          fixedLayout
          items={items}
          id="TransactionTable"
          onSort={handleSort}
          hasMore={hasMore}
          className="defi-transaction-dataTable"
          onLoadMore={handleLoadMore}
          noRowHover
        >
          <DataTableColumn
            isSorted={sortColumn === 'tx_hash'}
            label="Transaction ID"
            primaryColumn
            property="tx_hash"
            sortable
            sortDirection={sortColumnDirection.tx_hash}
          ></DataTableColumn>
          <DataTableColumn label="Chain" property="chain">
            <CustomDataTableChain />
          </DataTableColumn>
          <DataTableColumn
            isSorted={sortColumn === 'address'}
            label="Address/Protocol"
            property="address"
            sortable
            sortDirection={sortColumnDirection.address}
          >
            <CustomDataTableCellOne />
          </DataTableColumn>

          <DataTableColumn label="Notes" property="note" width="18rem">
            <CustomDataTableNotes />
          </DataTableColumn>
          <DataTableColumn label="Transaction Date" property="time_at">
            <CustomDataTableCell />
          </DataTableColumn>
        </DataTable>
      </div>
    </div>
  );
};

export default TransactionHistory;
